import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useConversationActivities } from '../contexts/ConversationActivitiesContext';
import api, { conversationService, patientService } from '../services/api';
import { formatDateTime, getTodayDate, getDateRange } from '../utils/dateUtils';
import { EventValue, EventSeenStatus, EventInternalRemark, WhatsAppButton } from '../components/ConversationActivities';

const ConversationActivities = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const {
    searchTerm,
    setSearchTerm,
    perPage,
    showUnseenOnly,
    setShowUnseenOnly,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    conversationIdFilter,
    setConversationIdFilter,
    clinicFilter,
    setClinicFilter,
    clearAllFilters,
    hasActiveFilters,
    getFilterParams,
  } = useConversationActivities();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [settingSeen, setSettingSeen] = useState({});
  const [groupingMode, setGroupingMode] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());

  // Checkbox selection state
  const [selectedEvents, setSelectedEvents] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [bulkSettingSeen, setBulkSettingSeen] = useState(false);

  // Debounced search
  const searchTimeoutRef = useRef(null);

  // Group events by conversation ID when grouping mode is enabled
  const groupedEvents = useCallback(() => {
    if (!groupingMode) return events;

    const groups = {};
    events.forEach(event => {
      const conversationId = event.conversation_id;
      if (!groups[conversationId]) {
        groups[conversationId] = [];
      }
      groups[conversationId].push(event);
    });

    // Sort events within each group by timestamp (newest first)
    Object.keys(groups).forEach(conversationId => {
      groups[conversationId].sort((a, b) => {
        const timeA = new Date(a.timestamp || 0);
        const timeB = new Date(b.timestamp || 0);
        return timeB - timeA;
      });
    });

    return groups;
  }, [events, groupingMode]);

    // Get display events (either grouped or ungrouped)
  const displayEvents = useCallback(() => {
    if (!groupingMode) return events;

    const groups = groupedEvents();
    const flattened = [];

    Object.entries(groups).forEach(([conversationId, groupEvents]) => {
      // Add a header row for the group
      if (groupEvents.length > 0) {
        const firstEvent = groupEvents[0];
        flattened.push({
          id: `group-${conversationId}`,
          isGroupHeader: true,
          conversation_id: conversationId,
          timestamp: groupEvents[0].timestamp,
          event_count: groupEvents.length,
          phone_number: extractPhoneNumber(conversationId),
          isCollapsed: collapsedGroups.has(conversationId)
        });
      }

      // Add all events in the group only if not collapsed
      if (!collapsedGroups.has(conversationId)) {
        groupEvents.forEach(event => {
          flattened.push({
            ...event,
            isGrouped: true
          });
        });
      }
    });

    return flattened;
  }, [events, groupingMode, groupedEvents, collapsedGroups]);

  // Handle group expand/collapse
  const toggleGroup = (conversationId) => {
    setCollapsedGroups(prev => {
      const newCollapsed = new Set(prev);
      if (newCollapsed.has(conversationId)) {
        newCollapsed.delete(conversationId);
      } else {
        newCollapsed.add(conversationId);
      }
      return newCollapsed;
    });
  };

  // Expand all groups
  const expandAllGroups = () => {
    setCollapsedGroups(new Set());
  };

  // Collapse all groups
  const collapseAllGroups = () => {
    const allConversationIds = new Set(events.map(event => event.conversation_id));
    setCollapsedGroups(allConversationIds);
  };

  // Select all events in a conversation group
  const handleSelectGroup = (conversationId) => {
    const groupEvents = events.filter(event => event.conversation_id === conversationId);
    const groupEventIds = groupEvents.map(event => event.id);

    setSelectedEvents(prev => {
      const newSelected = new Set(prev);
      const allGroupEventsSelected = groupEventIds.every(id => newSelected.has(id));

      if (allGroupEventsSelected) {
        // If all events in group are selected, deselect them
        groupEventIds.forEach(id => newSelected.delete(id));
      } else {
        // If not all events are selected, select all events in the group
        groupEventIds.forEach(id => newSelected.add(id));
      }

      return newSelected;
    });
  };

  // Check if all events in a conversation group are selected
  const isGroupFullySelected = (conversationId) => {
    const groupEvents = events.filter(event => event.conversation_id === conversationId);
    const groupEventIds = groupEvents.map(event => event.id);
    return groupEventIds.length > 0 && groupEventIds.every(id => selectedEvents.has(id));
  };

  // Check if some events in a conversation group are selected
  const isGroupPartiallySelected = (conversationId) => {
    const groupEvents = events.filter(event => event.conversation_id === conversationId);
    const groupEventIds = groupEvents.map(event => event.id);
    return groupEventIds.some(id => selectedEvents.has(id)) && !isGroupFullySelected(conversationId);
  };

  // Auto-collapse all groups when grouping mode is enabled or events change
  useEffect(() => {
    if (groupingMode && events.length > 0) {
      const allConversationIds = new Set(events.map(event => event.conversation_id));
      setCollapsedGroups(allConversationIds);
    }
  }, [groupingMode, events]);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // When grouping is enabled, we need to fetch more events to account for grouping
      // This ensures we get enough events to fill the page even after grouping
      const effectivePerPage = groupingMode ? perPage * 3 : perPage;

      const params = new URLSearchParams({
        page: currentPage,
        per_page: effectivePerPage,
      });

      // Get filter parameters from context
      const filterParams = getFilterParams();
      Object.entries(filterParams).forEach(([key, value]) => {
        params.append(key, value);
      });

      const response = await api.get(`/api/conversation-activities/?${params}`);

      // The API returns the data directly, not wrapped in a success field
      setEvents(response.data.events);
      setTotalPages(response.data.total_pages);
      setTotalItems(response.data.total);
    } catch (error) {
      console.error('Error fetching conversation activities:', error);
      setError(error.response?.data?.detail || error.message || t('conversationActivities.error'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, perPage, getFilterParams, groupingMode]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const handleSearchInputChange = (e) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    setCurrentPage(1);

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      // The fetchEvents will be triggered automatically due to searchTerm dependency
    }, 500); // 500ms delay
  };

  const handleUnseenOnlyChange = (checked) => {
    setShowUnseenOnly(checked);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };



  const handleDateRangeChange = (range) => {
    const { startDate: newStartDate, endDate: newEndDate } = getDateRange(range);
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setCurrentPage(1);
  };

  const handleClearDates = () => {
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  const handlePhoneNumberClick = (conversationId) => {
    setConversationIdFilter(conversationId);
    setCurrentPage(1);
  };

  const handlePatientNavigation = async (conversationId) => {
    try {
      // Get patient information by conversation ID
      const patientData = await patientService.getPatientByConversationId(conversationId);

      // Navigate directly to patient details page using the customer ID
      navigate(`/patients/${patientData.id}`);
    } catch (error) {
      console.error('Error fetching patient data:', error);
      // Fallback to the old method if the API call fails
      const extractedPhone = extractPhoneNumber(conversationId);
      navigate(`/patients?phone=${encodeURIComponent(extractedPhone)}`);
    }
  };

  const handleClearConversationFilter = () => {
    setConversationIdFilter('');
    setCurrentPage(1);
  };

  const handleClinicFilterChange = (e) => {
    setClinicFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleClearAllFilters = () => {
    clearAllFilters();
    setCurrentPage(1);
  };

  const handleSetSeen = async (eventId) => {
    try {
      setSettingSeen(prev => ({ ...prev, [eventId]: true }));

      await conversationService.setEventMetadata(eventId, 'seen', true);

      // Refresh the events list to show updated status
      await fetchEvents();

      // Show success message (you can add a toast notification here)
      console.log(`Event ${eventId} marked as seen`);
    } catch (error) {
      console.error('Error setting event as seen:', error);
      // Show error message (you can add a toast notification here)
    } finally {
      setSettingSeen(prev => ({ ...prev, [eventId]: false }));
    }
  };

  const getEventTypeLabel = (eventType, key) => {
    if (eventType === 'create' && key === 'notify_clinic_by_human') {
      return t('eventType.clinicNotification');
    } else if (eventType === 'leave_message' && key === 'notification') {
      return t('eventType.leaveMessage');
    } else if (eventType === 'create' && key === 'appointment') {
      return t('eventType.createAppointment');
    } else if (eventType === 'failed' && key === 'appointment') {
      return t('eventType.failedAppointment');
    } else if (eventType === 'create' && key === 'appointment_offer') {
      return t('eventType.createAppointmentOffer');
    } else if (eventType === 'confirm' && key === 'appointment_offer') {
      return t('eventType.confirmAppointmentOffer');
    } else if (eventType === 'create' && key === 'appointment_info') {
      return t('eventType.submitForm');
    } else if (eventType === 'update' && key === 'appointment_info') {
      return t('eventType.updateAppointmentInfo');
    } else if (eventType === 'create' && key === 'media_upload') {
      return t('eventType.mediaUpload');
    } else if (eventType === 'transcribe_voice_mail' && key === 'voice_mail') {
      return t('eventType.transcribeVoiceMail');
    } else if (eventType === 'escalate_message' && key === 'notification') {
      return t('eventType.escalateMessage');
    }
    return `${eventType} - ${key}`;
  };

  // Checkbox selection handlers
  const handleSelectEvent = (eventId) => {
    setSelectedEvents(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(eventId)) {
        newSelected.delete(eventId);
      } else {
        newSelected.add(eventId);
      }
      return newSelected;
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedEvents(new Set());
      setSelectAll(false);
    } else {
      // When grouping is enabled, only select actual events (not group headers)
      const allEventIds = groupingMode
        ? displayEvents().filter(event => !event.isGroupHeader).map(event => event.id)
        : events.map(event => event.id);
      setSelectedEvents(new Set(allEventIds));
      setSelectAll(true);
    }
  };

  const handleBulkSetSeen = async () => {
    if (selectedEvents.size === 0) return;

    try {
      setBulkSettingSeen(true);

      // Set seen for all selected events
      const promises = Array.from(selectedEvents).map(eventId =>
        conversationService.setEventMetadata(eventId, 'seen', true)
      );

      await Promise.all(promises);

      // Clear selection and refresh events
      setSelectedEvents(new Set());
      setSelectAll(false);
      await fetchEvents();

      console.log(`${selectedEvents.size} events marked as seen`);
    } catch (error) {
      console.error('Error setting events as seen:', error);
    } finally {
      setBulkSettingSeen(false);
    }
  };

  // Update select all state when events change
  useEffect(() => {
    if (events.length > 0) {
      // When grouping is enabled, only check actual events (not group headers)
      const eventsToCheck = groupingMode
        ? displayEvents().filter(event => !event.isGroupHeader)
        : events;

      if (eventsToCheck.length > 0) {
        const allSelected = eventsToCheck.every(event => selectedEvents.has(event.id));
        setSelectAll(allSelected);
      } else {
        setSelectAll(false);
      }
    } else {
      setSelectAll(false);
    }
  }, [events, selectedEvents, groupingMode, displayEvents]);

  const extractPhoneNumber = (conversationId) => {
    if (conversationId && conversationId.includes('::')) {
      const parts = conversationId.split('::');
      if (parts.length >= 3) {
        return parts[2]; // Phone number is typically the third part
      }
    }
    return conversationId; // Fallback to full conversation ID if parsing fails
  };



  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          {t('conversationActivities.title')}
        </h1>
        <p className="text-gray-600">
          {t('conversationActivities.subtitle')}
        </p>
      </div>

      {/* Search and Filter Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <form onSubmit={handleSearch} className="space-y-6">
          {/* Search Bar Row */}
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-end">
            {/* Main Search Input */}
            <div className="flex-1 min-w-0">
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
                {t('common.search')}
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="search"
                  value={searchTerm}
                  onChange={handleSearchInputChange}
                  placeholder={t('conversationActivities.search')}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>



            {/* Show Unseen Only Toggle */}
            <div className="w-full lg:w-auto">
              <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showUnseenOnly}
                  onChange={(e) => handleUnseenOnlyChange(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-3 text-sm font-medium text-gray-700">{t('conversationActivities.showUnseenOnly')}</span>
              </label>
            </div>

            {/* Grouping Mode Toggle */}
            <div className="w-full lg:w-auto">
              <label className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200 cursor-pointer">
                <input
                  type="checkbox"
                  checked={groupingMode}
                  onChange={(e) => {
                    const newGroupingMode = e.target.checked;
                    setGroupingMode(newGroupingMode);

                    // When enabling grouping, collapse all groups by default
                    if (newGroupingMode && events.length > 0) {
                      const allConversationIds = new Set(events.map(event => event.conversation_id));
                      setCollapsedGroups(allConversationIds);
                    }
                  }}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-3 text-sm font-medium text-gray-700">{t('conversationActivities.groupByConversation')}</span>
              </label>
            </div>

            {/* Group Control Buttons */}
            {groupingMode && (
              <div className="w-full lg:w-auto flex gap-2">
                <button
                  type="button"
                  onClick={expandAllGroups}
                  className="px-3 py-2 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors duration-200 border border-green-200 flex items-center space-x-2"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                  <span>{t('conversationActivities.expandAll')}</span>
                </button>
                <button
                  type="button"
                  onClick={collapseAllGroups}
                  className="px-3 py-2 text-sm bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors duration-200 border border-orange-200 flex items-center space-x-2"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                  </svg>
                  <span>{t('conversationActivities.collapseAll')}</span>
                </button>
              </div>
            )}
          </div>

          {/* Date Filter Row */}
          <div className="border-t border-gray-200 pt-6">
            <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-end">
              <div className="flex-1">
                <h3 className="text-sm font-medium text-gray-700 mb-3">{t('conversationActivities.dateFilter')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="startDate" className="block text-sm font-medium text-gray-600 mb-2">
                      {t('conversationActivities.dateFrom')}
                    </label>
                    <input
                      type="date"
                      id="startDate"
                      value={startDate}
                      onChange={(e) => {
                        setStartDate(e.target.value);
                        setCurrentPage(1);
                      }}
                      max={endDate || getTodayDate()}
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
                    />
                  </div>

                  <div>
                    <label htmlFor="endDate" className="block text-sm font-medium text-gray-600 mb-2">
                      {t('conversationActivities.dateTo')}
                    </label>
                    <input
                      type="date"
                      id="endDate"
                      value={endDate}
                      onChange={(e) => {
                        setEndDate(e.target.value);
                        setCurrentPage(1);
                      }}
                      min={startDate}
                      max={getTodayDate()}
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
                    />
                  </div>
                </div>
              </div>

              <div className="w-full lg:w-auto">
                <label className="block text-sm font-medium text-gray-600 mb-2">{t('conversationActivities.quickFilters')}</label>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => handleDateRangeChange(7)}
                    className="px-4 py-2 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200 border border-blue-200"
                  >
                    {t('conversationActivities.last7Days')}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDateRangeChange(30)}
                    className="px-4 py-2 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200 border border-blue-200"
                  >
                    {t('conversationActivities.last30Days')}
                  </button>
                  <button
                    type="button"
                    onClick={handleClearDates}
                    className="px-4 py-2 text-sm bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors duration-200 border border-gray-200"
                  >
                    {t('conversationActivities.clear')}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Clear All Filters Button */}
          {hasActiveFilters && (
            <div className="border-t border-gray-200 pt-6">
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleClearAllFilters}
                  className="px-4 py-2 text-sm bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors duration-200 border border-gray-200 flex items-center space-x-2"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span>{t('conversationActivities.clearAllFilters')}</span>
                </button>
              </div>
            </div>
          )}

          {/* Conversation ID Filter Row */}
          {conversationIdFilter && (
            <div className="border-t border-gray-200 pt-6">
              <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-end">
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">{t('conversationActivities.conversationIdFilter')}</h3>
                  <div className="flex items-center space-x-3">
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        id="conversationIdFilter"
                        value={conversationIdFilter}
                        onChange={(e) => setConversationIdFilter(e.target.value)}
                        placeholder={t('conversationActivities.filteredByConversationId')}
                        className="w-full px-4 py-3 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-blue-50 transition-colors duration-200"
                        readOnly
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        <svg className="h-5 w-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
                        </svg>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleClearConversationFilter}
                      className="px-4 py-3 text-sm bg-red-50 text-red-700 rounded-lg hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors duration-200 border border-red-200 flex items-center space-x-2"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span>{t('conversationActivities.clearFilter')}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </form>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Events Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">{t('conversationActivities.loadingActivities')}</p>
          </div>
        ) : (
          <>
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('conversationActivities.conversationActivities')} ({totalItems} {t('conversationActivities.total')})
                  {groupingMode && (
                    <span className="ml-2 text-sm font-normal text-gray-600">
                      - {displayEvents().filter(event => !event.isGroupHeader).length} {t('conversationActivities.events')} {t('conversationActivities.displayed')}
                    </span>
                  )}
                </h2>
                {selectedEvents.size > 0 && (
                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-gray-600">
                      {selectedEvents.size} {t('conversationActivities.selected')}
                    </span>
                    <button
                      onClick={handleBulkSetSeen}
                      disabled={bulkSettingSeen}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {bulkSettingSeen ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {t('conversationActivities.setting')}
                        </>
                      ) : (
                        t('conversationActivities.setSeenForSelected')
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {events.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-gray-500">{t('conversationActivities.noActivitiesFound')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={handleSelectAll}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('conversationActivities.timestamp')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('conversationActivities.eventType')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('conversationActivities.phoneNumber')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('conversationActivities.message')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('conversationActivities.internalRemark')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('conversationActivities.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {displayEvents().map((event) => {
                      // Render group header
                      if (event.isGroupHeader) {
                        const isFullySelected = isGroupFullySelected(event.conversation_id);
                        const isPartiallySelected = isGroupPartiallySelected(event.conversation_id);

                        return (
                          <tr key={event.id} className="bg-blue-50 hover:bg-blue-100">
                            <td className="px-6 py-3" colSpan="7">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                  <button
                                    onClick={() => toggleGroup(event.conversation_id)}
                                    className="flex-shrink-0 p-1 hover:bg-blue-100 rounded transition-colors duration-200"
                                    title={event.isCollapsed ? t('conversationActivities.expandGroup') : t('conversationActivities.collapseGroup')}
                                  >
                                    <svg
                                      className={`h-5 w-5 text-blue-600 transition-transform duration-200 ${event.isCollapsed ? 'rotate-90' : ''}`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                  </button>

                                  {/* Group Selection Checkbox */}
                                  <div className="flex-shrink-0">
                                    <input
                                      type="checkbox"
                                      checked={isFullySelected}
                                      ref={(input) => {
                                        if (input) {
                                          input.indeterminate = isPartiallySelected;
                                        }
                                      }}
                                      onChange={() => handleSelectGroup(event.conversation_id)}
                                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                      title={isFullySelected ? t('conversationActivities.deselectGroup') : t('conversationActivities.selectGroup')}
                                    />
                                  </div>

                                  <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14H9a2 2 0 01-2-2V6a2 2 0 012-2h6a2 2 0 012 2v6a2 2 0 01-2 2h-2l-2 2z" />
                                    </svg>
                                  </div>
                                  <div>
                                    <span className="text-sm font-medium text-blue-900">
                                      {t('conversationActivities.conversationGroup')}: {event.phone_number}
                                    </span>
                                    <span className="ml-2 text-xs text-blue-700 bg-blue-100 px-2 py-1 rounded-full">
                                      {event.event_count} {t('conversationActivities.events')}
                                    </span>
                                  </div>
                                </div>
                                <div className="text-xs text-blue-600">
                                  {event.timestamp ? formatDateTime(event.timestamp) : 'N/A'}
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      }

                      // Render regular event row
                      return (
                        <tr key={event.id} className={`hover:bg-gray-50 ${event.isGrouped ? 'bg-gray-25' : ''}`}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {!event.isGroupHeader && (
                              <input
                                type="checkbox"
                                checked={selectedEvents.has(event.id)}
                                onChange={() => handleSelectEvent(event.id)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {event.timestamp ? formatDateTime(event.timestamp) : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {getEventTypeLabel(event.event_type, event.key)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                            <div className="flex items-center space-x-2">
                              <button
                                // onClick={() => handlePhoneNumberClick(event.conversation_id)}
                                className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded"
                                title={t('conversationActivities.clickToFilterByConversation')}
                              >
                                {extractPhoneNumber(event.conversation_id)}
                              </button>

                              <button
                                onClick={() => handlePatientNavigation(event.conversation_id)}
                                className="text-green-600 hover:text-green-800 hover:underline cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1 rounded text-xs"
                                title={t('conversationActivities.viewPatientDetails')}
                              >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                              </button>

                              <WhatsAppButton
                                phoneNumber={extractPhoneNumber(event.conversation_id)}
                                size="sm"
                              />
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900 max-w-md">
                            <div className="flex items-center gap-2">
                              <EventValue event={event} />
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            <EventInternalRemark
                              event={event}
                              onUpdate={fetchEvents}
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="flex items-center gap-2">
                              <EventSeenStatus
                                event={event}
                                onSetSeen={handleSetSeen}
                                isSettingSeen={settingSeen[event.id]}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    {t('conversationActivities.showingPage')} {currentPage} {t('conversationActivities.of')} {totalPages} ({totalItems} {t('conversationActivities.totalItems')})
                    {groupingMode && (
                      <span className="ml-2 text-gray-600">
                        - {displayEvents().filter(event => !event.isGroupHeader).length} {t('conversationActivities.events')} {t('conversationActivities.displayed')}
                      </span>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      {t('conversationActivities.previous')}
                    </button>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`px-3 py-1 text-sm border rounded-md ${currentPage === page
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      {t('conversationActivities.next')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ConversationActivities;
